import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Eye, ThumbsUp, Coins } from 'lucide-react';
import resumeTemplatesApi from '../../services/resumeTemplatesApi';
import AlertModal from '../common/AlertModal';
import {
  Page,
  PageGrid,
  SmallPreviewCol,
  SmallPreview,
  Layout,
  PreviewCol,
  PreviewImg,
  InfoCol,
  Name,
  DownloadButton,
  Divider,
  StatsRow,
  Stat,
  SectionTitle,
  InfoTable,
  InfoKey,
  InfoVal,
  TagList,
  Tag,
  Box,
  ErrorBox,
} from './ResumeTemplateDetail.styles';

const ResumeTemplateDetail = () => {
  const { id } = useParams();
  const templateId = id;  // 现在ID是UUID字符串

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tpl, setTpl] = useState(null);
  const [liked, setLiked] = useState(false);

  const [quota, setQuota] = useState(null);
  const [quotaError, setQuotaError] = useState('');

  const [downloading, setDownloading] = useState(false);

  const [liking, setLiking] = useState(false);

  const [activePageNo, setActivePageNo] = useState(1);

  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '', type: 'error' });

  /** ===== 数据处理 ===== */
  const tags = useMemo(() => {
    const t = tpl?.tags;
    if (!t) return [];
    // 安全处理：清理标签中的特殊字符，防止XSS
    return String(t)
      .split(/[,，\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map(tag => {
        // 移除HTML标签和特殊字符
        return tag.replace(/[<>'"&]/g, '');
      })
      .filter(tag => tag.length > 0 && tag.length < 50); // 限制标签长度
  }, [tpl]);

  const loadDetail = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const res = await resumeTemplatesApi.getDetail(templateId);
      setTpl(res?.data || null);
      setLiked(Boolean(res?.data?.liked));
    } catch (e) {
      setError(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  const loadQuota = useCallback(async () => {
    try {
      setQuotaError('');
      const res = await resumeTemplatesApi.getQuota();
      setQuota(res?.data || null);
    } catch (e) {
      // 额度查询失败不阻断页面
      setQuotaError(e?.message || '获取额度失败');
      setQuota(null);
    }
  }, []);

  useEffect(() => {
    if (!templateId) {
      setError('无效模板ID');
      setLoading(false);
      return;
    }
    loadDetail();
    loadQuota();
  }, [templateId, loadDetail, loadQuota]);

  const hasIncrementedViewRef = React.useRef(false);
  useEffect(() => {
    if (!templateId) return;
    if (hasIncrementedViewRef.current) return;
    hasIncrementedViewRef.current = true;
    resumeTemplatesApi.incrementView(templateId).catch(() => { });
  }, [templateId]);

  /** ===== 下载 ===== */
  const onDownload = async () => {
    if (!tpl) return;
    if (downloading) return; // 防止重复点击
    
    try {
      setDownloading(true);

      const response = await resumeTemplatesApi.downloadBlob(templateId);
      
      // 检查是否是OSS下载（返回JSON包含download_url）
      if (response?.data?.download_url) {
        // 安全检查：确保URL是有效的HTTP(S) URL
        const downloadUrl = response.data.download_url;
        try {
          const url = new URL(downloadUrl);
          if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            throw new Error('不安全的下载URL');
          }
        } catch (e) {
          console.error('下载URL验证失败:', e);
          throw new Error('下载链接格式错误');
        }
        
        // OSS下载：使用服务器返回的文件名，直接跳转到预签名URL
        // OSS预签名URL已包含Content-Disposition参数，会自动使用正确的文件名下载
        window.location.href = downloadUrl;
        
        // 延迟重置下载状态，给浏览器时间触发下载
        setTimeout(() => {
          setDownloading(false);
        }, 1000);
      } else if (response instanceof Blob) {
        // 本地文件下载：使用blob方式
        const url = window.URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = url;
        // 使用规范的中文文件名（已在后端清理过）
        const safeName = (tpl.name || 'resume').replace(/[<>:"/\\|?*]/g, '_');
        a.download = `${safeName}.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setDownloading(false);
      } else {
        setDownloading(false);
        throw new Error('未知的下载响应格式');
      }

      // 刷新配额和统计（不阻塞下载）
      loadQuota().catch(() => {});
      loadDetail().catch(() => {});
    } catch (e) {
      setDownloading(false);
      
      const msg = e?.message || '下载失败';
      const status = e?.response?.status || e?.status;
      let errorTitle = '下载失败';
      let errorMsg = '下载失败，请稍后重试';
      let errorType = 'error';
      
      // 处理控频错误（429）
      if (status === 429 || msg.includes('DAILY_DOWNLOAD_LIMIT_EXCEEDED') || msg.includes('429')) {
        errorTitle = '下载次数已达上限';
        errorMsg = '您今日的下载次数已用完，请明天再试。\n\n如需更多下载次数，请考虑升级会员等级。';
        errorType = 'warning';
      } else if (msg.includes('需要重新登录') || msg.includes('401') || status === 401) {
        errorTitle = '登录已过期';
        errorMsg = '您的登录已过期，请重新登录后再试。';
        errorType = 'warning';
      } else if (msg.includes('403') || status === 403) {
        errorTitle = '权限不足';
        errorMsg = '您没有权限下载此模板。';
        errorType = 'warning';
      } else if (msg.includes('404') || status === 404) {
        errorTitle = '模板不存在';
        errorMsg = '该模板文件不存在，可能已被删除。';
        errorType = 'warning';
      } else if (msg.includes('500') || status === 500 || msg.includes('DOWNLOAD_FAILED')) {
        errorTitle = '服务器错误';
        errorMsg = '服务器暂时无法处理您的请求，请稍后重试。\n\n如果问题持续存在，请联系客服。';
        errorType = 'error';
      } else if (msg) {
        // 避免显示原始错误信息（可能包含敏感信息）
        errorMsg = msg.length > 100 ? '下载失败，请稍后重试' : msg;
      }
      
      setErrorModal({
        isOpen: true,
        title: errorTitle,
        message: errorMsg,
        type: errorType
      });
    }
  };

  const normalizePreviewPath = useCallback((p) => {
    if (!p) return '';
    const raw = String(p).replace(/^\//, '');
    
    // 安全检查：防止XSS和路径遍历
    // 只允许http/https URL或相对路径
    if (raw.includes('<') || raw.includes('>') || raw.includes('javascript:') || raw.includes('data:')) {
      console.warn('检测到不安全的预览路径:', raw);
      return '';
    }
    
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;

    // 预览图静态资源挂在后端的 /assets 下。
    // 开发环境通常前后端端口不同，需要把图片请求指向后端同源(同 host:port)。
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const apiPort = process.env.REACT_APP_API_PORT || '8000';
    const base = process.env.NODE_ENV === 'production' ? '' : `${protocol}//${hostname}:${apiPort}`;

    if (raw.startsWith('assets/')) return `${base}/${raw}`;
    return `${base}/assets/${raw}`;
  }, []);

  const previewImages = useMemo(() => {
    const arr = tpl?.preview_images;
    if (Array.isArray(arr) && arr.length > 0) {
      return arr
        .slice()
        .sort((a, b) => (a?.page_no ?? 0) - (b?.page_no ?? 0))
        .map((x) => ({
          page_no: x?.page_no,
          src: normalizePreviewPath(x?.image_path),
        }))
        .filter((x) => x.page_no && x.src);
    }

    const fallback = normalizePreviewPath(tpl?.preview_image_path);
    return fallback ? [{ page_no: 1, src: fallback }] : [];
  }, [tpl, normalizePreviewPath]);

  useEffect(() => {
    if (!tpl) return;
    const cover = Number(tpl?.cover_page_no || 0);
    const defaultPageNo = cover > 0 ? cover : 1;
    const exists = previewImages.some((x) => x.page_no === defaultPageNo);
    setActivePageNo(exists ? defaultPageNo : (previewImages[0]?.page_no || 1));
  }, [tpl, previewImages]);

  const activePreviewSrc = useMemo(() => {
    const found = previewImages.find((x) => x.page_no === activePageNo);
    return found?.src || previewImages[0]?.src || '';
  }, [previewImages, activePageNo]);

  /** ====== 渲染 ====== */
  if (loading) return <Box>加载中...</Box>;
  if (error) return <ErrorBox>{error}</ErrorBox>;
  if (!tpl) return <Box>模板不存在</Box>;

  return (
    <>
      {/* 主体 5 列布局 - 新标签页打开，带侧边栏，支持多模板对比 */}
      <PageGrid>
        {/* ①左空白 */}
        <div />

        {/* ②小图预览列 */}
        <SmallPreviewCol>
          {previewImages.map((img) => (
            <SmallPreview
              key={img.page_no}
              src={img.src || '/images/placeholders/empty.png'}
              alt={`${tpl.name} - 第 ${img.page_no} 页`}
              style={{
                border: `2px solid ${activePageNo === img.page_no ? '#6366f1' : 'transparent'}`,
                borderRadius: '8px',
                marginBottom: '8px',
                transition: 'border-color 0.2s ease',
              }}
              onClick={() => setActivePageNo(img.page_no)}
            />
          ))}
        </SmallPreviewCol>

        {/* ③大图预览列 */}
        <PreviewCol>
          <PreviewImg
            src={activePreviewSrc || '/images/placeholders/empty.png'}
            alt={`${tpl.name} - 第 ${activePageNo} 页`}
          />
        </PreviewCol>

        {/* ④信息面板列 */}
        <InfoCol>
          <Name>{tpl.name}</Name>
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <DownloadButton onClick={onDownload} disabled={downloading || (quota && quota.remaining <= 0)}>
              <span style={{ flex: 1, textAlign: 'center' }}>{downloading ? '准备下载...' : '立即下载'}</span>
              {quota && (
                <span>
                  <Coins size={14} /> {quota.remaining}/{quota.daily_limit}
                </span>
              )}
            </DownloadButton>
          </div>
          <Divider />
          <StatsRow>
            <Stat>
              <Download /> {tpl.download_count ?? 0}
            </Stat>
            <Stat>
              <Eye /> {tpl.view_count ?? 0}
            </Stat>
            <Stat
              style={{ cursor: 'pointer', opacity: liking ? 0.7 : 1 }}
              onClick={async () => {
                if (liking) return;
                try {
                  setLiking(true);
                  const res = await resumeTemplatesApi.toggleLike(templateId);
                  const nextLike = res?.data?.like_count;
                  const nextLiked = Boolean(res?.data?.liked);
                  setLiked(nextLiked);
                  setTpl((prev) => (prev ? { ...prev, like_count: typeof nextLike === 'number' ? nextLike : (prev.like_count ?? 0) } : prev));
                } catch (e) {
                  const msg = e?.message || '点赞失败';
                  alert(msg.includes('需要重新登录') ? '需要重新登录' : msg);
                } finally {
                  setLiking(false);
                }
              }}
              title={liked ? '取消点赞' : '点赞'}
            >
              <ThumbsUp
                size={18}
                fill={liked ? '#ef4444' : 'none'}
                color={liked ? '#ef4444' : 'currentColor'}
              />
              {tpl.like_count ?? 0}
            </Stat>
          </StatsRow>
          <Divider />
          <SectionTitle>简历信息</SectionTitle>
          <InfoTable>
            <InfoKey>简介</InfoKey>
            <InfoVal>{tpl.intro || '-'}</InfoVal>
            <InfoKey>分类</InfoKey>
            <InfoVal>{tpl.category || '-'}</InfoVal>
            <InfoKey>标签</InfoKey>
            <InfoVal>
              {tags.length ? <TagList>{tags.map((t) => <Tag key={t}>{t}</Tag>)}</TagList> : '-'}
            </InfoVal>
          </InfoTable>
        </InfoCol>

        {/* ⑤右空白 */}
        <div />
      </PageGrid>

      {/* 错误提示弹窗 */}
      <AlertModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
        confirmText="确定"
      />
    </>
  );
};

export default ResumeTemplateDetail;
